import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Loader2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerDuplicates = ({ onDone }) => {
  const [pairs, setPairs] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/lists/customers/duplicates`).then((r) => {
    setPairs(r.data.pairs || []);
    setPicked(new Set((r.data.pairs || []).map((p) => p.duplicate_id)));
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!pairs.length) return null;

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const merge = async () => {
    if (!window.confirm(`Merge ${picked.size} duplicate records into the supplier companies? Their contacts and deals move across and the duplicates are removed.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/lists/customers/duplicates/merge`, { duplicate_ids: [...picked] });
      toast.success(`Merged ${r.data.merged} duplicates, ${r.data.contacts_moved} contacts moved`);
      setOpen(false);
      await load();
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not merge duplicates'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5 space-y-2" data-testid="customer-duplicates-banner">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-sky-900 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5 shrink-0" />{pairs.length} customer {pairs.length === 1 ? 'company has' : 'companies have'} a duplicate record in the CRM.</span>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} className="border-sky-300 text-sky-800 shrink-0" data-testid="customer-duplicates-review-btn">{open ? 'Hide' : 'Review & merge'}</Button>
      </div>
      {open && (
        <>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {pairs.map((p) => (
              <label key={p.duplicate_id} className="flex items-center gap-2 text-xs text-slate-700 bg-white rounded px-2 py-1.5 cursor-pointer" data-testid={`dup-pair-${p.duplicate_id}`}>
                <input type="checkbox" checked={picked.has(p.duplicate_id)} onChange={() => toggle(p.duplicate_id)} className="accent-[#00A79D]" />
                <span className="truncate">{p.duplicate_name}</span>
                <span className="text-slate-400 shrink-0">({p.contacts} contacts{p.deals ? `, ${p.deals} deals` : ''})</span>
                <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                <span className="font-semibold truncate">{p.keep_name}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={merge} disabled={busy || !picked.size} className="bg-[#00A79D] hover:bg-[#00857D] text-white" data-testid="customer-duplicates-merge-btn">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Merge {picked.size} selected
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
